import { useMemo } from 'react';
import { ReportPhotoDTO } from '../photos/types';

export interface StepCompletionStatus {
  isComplete: boolean;
  hasWarning: boolean;
  warningMessage?: string;
}

interface UseStepCompletionStatusProps<TPhoto extends ReportPhotoDTO = ReportPhotoDTO> {
  photos: TPhoto[];
  photosLoaded: boolean;
  budgetSummary?: { totalAmountReccForFunding?: number } | null;
  invoices?: Array<{ currentInvoice?: boolean }> | null;
  completionStatusVersion?: number;
  invoiceStepCompleted?: boolean;
}

/**
 * Shared hook for calculating step completion status
 * Used by both Magic and Limited report wizards
 */
export const useStepCompletionStatus = <TPhoto extends ReportPhotoDTO = ReportPhotoDTO>({
  photos,
  photosLoaded,
  budgetSummary,
  invoices,
  completionStatusVersion,
  invoiceStepCompleted,
}: UseStepCompletionStatusProps<TPhoto>): StepCompletionStatus[] => {
  return useMemo(() => {
    if (!photosLoaded) {
      return Array(5).fill({ isComplete: false, hasWarning: false });
    }

    const statuses: StepCompletionStatus[] = [];

    // Step 0: Photos - warning if no photos or missing captions
    const photosWithoutCaptions = photos.filter(photo => !photo.hasCaption || !photo.caption?.trim());
    const hasPhotos = photos.length > 0;
    const hasAllCaptions = photosWithoutCaptions.length === 0;

    statuses[0] = {
      isComplete: hasPhotos && hasAllCaptions,
      hasWarning: !hasPhotos || photosWithoutCaptions.length > 0,
      warningMessage: !hasPhotos ? 'No photos uploaded' : photosWithoutCaptions.length > 0 ? `photo(s) missing captions` : undefined,
    };

    // Step 1: Review Budget - warning if no recommendation for funding or 0 value
    const hasRecommendationForFunding = budgetSummary?.totalAmountReccForFunding && budgetSummary.totalAmountReccForFunding > 0;

    statuses[1] = {
      isComplete: !!hasRecommendationForFunding,
      hasWarning: !hasRecommendationForFunding,
      warningMessage: !hasRecommendationForFunding ? 'No recommendation for funding amount set' : undefined,
    };

    // Step 2: Add Notes (limited wizard) / unused (magic wizard) — neutral
    statuses[2] = {
      isComplete: false,
      hasWarning: false,
    };

    // Step 3: Invoice - warning if no current invoice
    const hasCurrentInvoice = invoices?.some(invoice => invoice.currentInvoice) || false;
    const isInvoiceStepComplete = invoiceStepCompleted !== undefined ? invoiceStepCompleted && hasCurrentInvoice : hasCurrentInvoice;

    statuses[3] = {
      isComplete: isInvoiceStepComplete,
      hasWarning: !hasCurrentInvoice,
      warningMessage: !hasCurrentInvoice ? 'No current invoice set' : undefined,
    };

    // Step 4: Preview - neutral step (no completion criteria)
    statuses[4] = {
      isComplete: false,
      hasWarning: false,
    };

    return statuses;
  }, [photos, photosLoaded, budgetSummary, invoices, completionStatusVersion, invoiceStepCompleted]);
};
